import React, { useState } from 'react';
import { X, Edit, Target } from 'lucide-react';
import { Goal } from '../../types';
import { apiService } from '../../services/api';

interface EditGoalModalProps {
  goal: Goal;
  onClose: () => void;
  onSuccess: (updatedGoal: Goal) => void;
}

export const EditGoalModal: React.FC<EditGoalModalProps> = ({
  goal,
  onClose,
  onSuccess
}) => {
  const [formData, setFormData] = useState({
    name: goal.name,
    description: goal.description || '',
    estimatedHours: goal.estimatedHours || '',
    targetDate: goal.targetDate ? goal.targetDate.split('T')[0] : '',
    complexityScore: goal.complexityScore || '',
    status: goal.status,
    standalonePotential: goal.standalonePotential
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      setError('Goal name is required');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await apiService.updateGoal(goal.id, {
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
        estimatedHours: formData.estimatedHours ? parseInt(formData.estimatedHours.toString()) : undefined,
        targetDate: formData.targetDate || undefined,
        complexityScore: formData.complexityScore ? parseInt(formData.complexityScore.toString()) : undefined,
        status: formData.status,
        standalonePotential: formData.standalonePotential
      });

      onSuccess(response.goal);
    } catch (err) {
      console.error('Goal update failed:', err);
      setError('Failed to update goal');
    } finally {
      setLoading(false);
    }
  };

  const statusOptions = [
    { value: 'not_started', label: 'Not Started' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'completed', label: 'Completed' },
    { value: 'blocked', label: 'Blocked' }
  ];

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose} />
        
        <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full">
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <Edit className="w-6 h-6 text-blue-600" />
              <h2 className="text-xl font-semibold text-gray-900">
                Edit Goal
              </h2>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-6">
            {error && (
              <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                {error}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Goal Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="input-field"
                  placeholder="Enter goal name..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="input-field"
                  placeholder="Describe this goal..."
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Estimated Hours
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={formData.estimatedHours}
                    onChange={(e) => setFormData({ ...formData, estimatedHours: e.target.value })}
                    className="input-field"
                    placeholder="Hours"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Complexity (1-10)
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="10"
                    value={formData.complexityScore}
                    onChange={(e) => setFormData({ ...formData, complexityScore: e.target.value })}
                    className="input-field"
                    placeholder="1-10"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Status
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value as 'not_started' | 'in_progress' | 'completed' | 'blocked' })}
                    className="input-field"
                  >
                    {statusOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Target Date
                  </label>
                  <input
                    type="date"
                    value={formData.targetDate}
                    onChange={(e) => setFormData({ ...formData, targetDate: e.target.value })}
                    className="input-field"
                  />
                </div>
              </div>

              <div className="flex items-start">
                <div className="flex items-center h-5">
                  <input
                    id="standalonePotential"
                    type="checkbox"
                    checked={formData.standalonePotential}
                    onChange={(e) => setFormData({ ...formData, standalonePotential: e.target.checked })}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                </div>
                <div className="ml-3 text-sm">
                  <label htmlFor="standalonePotential" className="font-medium text-gray-700">
                    Standalone Potential
                  </label>
                  <p className="text-gray-500">
                    This goal could work as an independent project
                  </p>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-6 border-t border-gray-200 mt-6">
              <button
                type="button"
                onClick={onClose}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="btn-primary flex items-center gap-2"
              >
                <Target className="w-4 h-4" />
                {loading ? 'Saving...' : 'Save Goal'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};